import { useEffect, useState } from "react";
import { useApi } from "../useApi";
import { isErr } from "../../Utils/Result";
import { AddressSuggestion } from "../../Types-DTOs/Address";

export const ADDRESS_SUGGESTIONS_MIN_LENGTH = 3;
const DEBOUNCE_MS = 300;

/**
 * Address suggestions for what the user is typing, after a short pause.
 *
 * A failed lookup just means no suggestions: the field keeps working as a
 * plain text input, so nobody is told about it.
 *
 * @param query What the user has typed; below 3 characters nothing is looked up.
 * @returns The suggestions for the latest query, empty while there are none.
 */
export const useAddressSuggestions = (query: string): AddressSuggestion[] => {
  const { suggestAddressesEndpoint } = useApi();
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < ADDRESS_SUGGESTIONS_MIN_LENGTH) {
      setSuggestions([]);
      return;
    }

    let isCurrent = true;
    const timer = setTimeout(async () => {
      const result = await suggestAddressesEndpoint(trimmed);
      if (!isCurrent) return;
      setSuggestions(isErr(result) ? [] : result.value.suggestions);
    }, DEBOUNCE_MS);

    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [query, suggestAddressesEndpoint]);

  return suggestions;
};
